import type { EstimateStatus } from '@/types/estimate';

export const EDITABLE_STATUSES: EstimateStatus[] = ['DRAFT', 'REVIEW', 'REVISED'];
const SENDABLE_STATUSES: EstimateStatus[] = ['DRAFT', 'REVIEW', 'REVISED'];
const ACTIONABLE_STATUSES: EstimateStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP', 'REVISED'];
const REVISABLE_STATUSES: EstimateStatus[] = ['SENT', 'VIEWED', 'REVISED', 'EXPIRED', 'DECLINED'];
const FOLLOW_UPABLE_STATUSES: EstimateStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP'];
/**
 * An estimate that authorizes an existing job (`linkedJobId`) can only be
 * deleted while it is still an internal draft — mirrors the backend guard in
 * `EstimatesService.softDelete`. Once the customer has seen it, it is the
 * job's approval record.
 */
const DELETABLE_LINKED_JOB_STATUSES: EstimateStatus[] = ['DRAFT', 'REVIEW'];
const CUSTOMER_VIEWABLE_STATUSES: EstimateStatus[] = [
  'SENT',
  'VIEWED',
  'FOLLOW_UP',
  'APPROVED',
  'DECLINED',
  'EXPIRED',
  'CONVERTED',
];

export type EstimateQuickAction =
  | 'edit'
  | 'send'
  | 'approve'
  | 'sendAgain'
  | 'collectSignature'
  | 'collectDeposit'
  | 'convertToJob'
  | 'convertToInvoice'
  | 'duplicate'
  | 'previewPDF'
  | 'copyLink';

export type EstimateOverflowAction =
  | 'edit'
  | 'send'
  | 'sendAgain'
  | 'approve'
  | 'collectSignature'
  | 'reject'
  | 'revise'
  | 'followUp'
  | 'convertToInvoice'
  | 'convertToJob'
  | 'collectDeposit'
  | 'paymentSchedule'
  | 'previewPDF'
  | 'copyLink'
  | 'downloadPDF'
  | 'duplicate'
  | 'delete';

export interface ResolveEstimateActionVisibilityOptions {
  status?: EstimateStatus;
  sentAt?: string | Date | null;
  variant: 'row' | 'header';
  isDesktop: boolean;
  canCollectPayment: boolean;
  depositAmount?: number;
  depositCollected?: boolean;
  depositRequired?: boolean;
  paymentScheduleAvailable?: boolean;
  linkedJobId?: string | null;
  linkedJobInactive?: boolean;
  /**
   * Whether an APPROVED estimate's approval has already become work. Either
   * one removes the Edit affordance: the job owns the scope from then on.
   * Mirrors `approvalDismissalBlocker` in operations.
   */
  fulfilledAt?: string | Date | null;
  convertedJobId?: string | null;
  canEditEstimate: boolean;
  canCreateEstimate: boolean;
  canDeleteEstimate: boolean;
  canEditJobs: boolean;
  canCreateJobs: boolean;
  canCreateInvoices: boolean;
  approvalRequiresJobEdit: boolean;
}

export interface EstimateActionVisibility {
  isEditable: boolean;
  isSendable: boolean;
  isActionable: boolean;
  isApproved: boolean;
  /** APPROVED, and still editable — saving an edit dismisses the approval. */
  isApprovalDismissible: boolean;
  depositDue: boolean;
  depositPreApproveDue: boolean;
  overflow: Record<EstimateOverflowAction, boolean>;
  quickActions: EstimateQuickAction[];
}

export function resolveEstimateActionVisibility({
  status,
  sentAt,
  variant,
  isDesktop,
  canCollectPayment,
  depositAmount,
  depositCollected,
  depositRequired,
  paymentScheduleAvailable = false,
  linkedJobId,
  linkedJobInactive = false,
  fulfilledAt,
  convertedJobId,
  canEditEstimate,
  canCreateEstimate,
  canDeleteEstimate,
  canEditJobs,
  canCreateJobs,
  canCreateInvoices,
  approvalRequiresJobEdit,
}: ResolveEstimateActionVisibilityOptions): EstimateActionVisibility {
  const isEditable = !status || EDITABLE_STATUSES.includes(status);
  /**
   * An APPROVED estimate can still be edited, and the edit DISMISSES the
   * approval (the backend archives the approved version and returns the row
   * to REVISED). Only until the approval has become work: a job created from
   * it, or its scope applied to the job it was raised on, owns the edit.
   */
  const isApprovalDismissible = status === 'APPROVED' && !fulfilledAt && !convertedJobId;
  const isSendable = !status || SENDABLE_STATUSES.includes(status);
  const isActionable = !!status && ACTIONABLE_STATUSES.includes(status);
  const isRevisable = !!status && REVISABLE_STATUSES.includes(status);
  const isFollowUpable = !!status && FOLLOW_UPABLE_STATUSES.includes(status);
  const isCustomerViewable =
    !!status &&
    CUSTOMER_VIEWABLE_STATUSES.includes(status) &&
    (status !== 'EXPIRED' || Boolean(sentAt));
  const isApproved = status === 'APPROVED';
  // Owed once approved, and still owed after the estimate became a job:
  // approval can convert it before the customer pays.
  const depositDue =
    (isApproved || status === 'CONVERTED') && (depositAmount ?? 0) > 0 && !depositCollected;
  const depositPreApproveDue =
    isActionable && !!depositRequired && (depositAmount ?? 0) > 0 && !depositCollected;
  const menuShowsAllActions = variant === 'row' || !isDesktop;
  const showActionableInOverflow = menuShowsAllActions || status === 'REVISED';
  const canApproveEstimate = canEditEstimate && (!approvalRequiresJobEdit || canEditJobs);
  /**
   * NOT-639 — who may record an approval, as opposed to who may collect one.
   *
   * The job is frequently sold at the kitchen table, and the office then has to
   * write down the yes the customer has already given. Gating Approve on
   * `isActionable` alone meant the only route out of DRAFT was Send, which
   * emails or texts a customer who has already agreed. A still-editable
   * estimate is therefore approvable too — the backend issues it and approves
   * it in one transition, recording no send, and the public link stays
   * available. Mirrors `OFFICE_APPROVABLE_STATUSES` in operations.
   *
   * `collectSignature` deliberately stays on `isActionable`. A signature is an
   * act the CUSTOMER performs on a document they were shown; there is nothing
   * to sign on a draft nobody has seen, and offering it here would invite a
   * signature captured against a document that was never presented.
   */
  const INTERNALLY_APPROVABLE = isActionable || isEditable;
  /**
   * NOT-639 — and where the action is allowed to appear.
   *
   * `showActionableInOverflow` exists to stop the desktop header listing an
   * item twice: there, actionable estimates promote Approve, Send-again and
   * Collect-signature to quick buttons, so the menu drops them. But the header's
   * DRAFT branch promotes Edit, Send and Preview — never Approve — so on a draft
   * that flag suppresses an item that has nowhere else to go. It is the second
   * of the two gates in the bug report, and the one that produced the symptom
   * exactly as filed: a draft's header menu offering only Payment Schedule,
   * Download PDF, Duplicate and Delete.
   */
  const showApproveInOverflow = showActionableInOverflow || !isActionable;

  const overflow: Record<EstimateOverflowAction, boolean> = {
    edit: canEditEstimate && menuShowsAllActions && (isEditable || isApprovalDismissible),
    send: canEditEstimate && menuShowsAllActions && isSendable,
    sendAgain: canEditEstimate && showActionableInOverflow && isActionable,
    approve: canApproveEstimate && showApproveInOverflow && INTERNALLY_APPROVABLE,
    collectSignature: canApproveEstimate && showActionableInOverflow && isActionable,
    reject: canEditEstimate && isActionable,
    revise: canEditEstimate && isRevisable && !linkedJobInactive,
    followUp: canEditEstimate && isFollowUpable,
    convertToInvoice:
      canCreateJobs && canCreateInvoices && menuShowsAllActions && isApproved && !linkedJobId,
    convertToJob: canCreateJobs && menuShowsAllActions && isApproved && !linkedJobId,
    collectDeposit:
      canEditEstimate &&
      canCollectPayment &&
      ((depositDue && menuShowsAllActions) || (depositPreApproveDue && showActionableInOverflow)),
    paymentSchedule:
      canEditEstimate && paymentScheduleAvailable && (isEditable || isActionable || isApproved),
    previewPDF: true,
    copyLink: isCustomerViewable,
    downloadPDF: true,
    duplicate: canCreateEstimate,
    delete:
      canDeleteEstimate &&
      (!linkedJobId || (!!status && DELETABLE_LINKED_JOB_STATUSES.includes(status))),
  };

  const quickActions: EstimateQuickAction[] = [];

  if (variant === 'header' && isDesktop) {
    if (canEditEstimate && isEditable && isSendable) {
      quickActions.push('edit', 'send', 'previewPDF');
      overflow.previewPDF = false;
    } else if (canEditEstimate && isActionable) {
      if (canApproveEstimate) quickActions.push('approve');
      quickActions.push('sendAgain');
      if (canApproveEstimate) {
        quickActions.push('collectSignature');
        overflow.collectSignature = false;
      }
      if (depositPreApproveDue && canCollectPayment) {
        quickActions.push('collectDeposit');
      }
    } else if (isApproved) {
      // Edit leads on an approved estimate the way it does on a draft. No
      // warning here: nothing changes until the form is SAVED, and the form is
      // where the dismissal is explained and confirmed.
      if (canEditEstimate && isApprovalDismissible) quickActions.push('edit');
      if (!linkedJobId && canCreateJobs) quickActions.push('convertToJob');
      if (!linkedJobId && canCreateJobs && canCreateInvoices) {
        quickActions.push('convertToInvoice');
      }
      if (depositDue && canCollectPayment && canEditEstimate) {
        quickActions.push('collectDeposit');
        overflow.collectDeposit = false;
      }
    } else if (status === 'DECLINED' || status === 'EXPIRED') {
      if (canCreateEstimate) quickActions.push('duplicate');
      quickActions.push('previewPDF');
      if (isCustomerViewable) quickActions.push('copyLink');
      if (canCreateEstimate) overflow.duplicate = false;
      overflow.previewPDF = false;
      overflow.copyLink = false;
    } else if (status === 'CONVERTED') {
      if (depositDue && canCollectPayment && canEditEstimate) {
        quickActions.push('collectDeposit');
        overflow.collectDeposit = false;
      }
      quickActions.push('previewPDF');
      if (isCustomerViewable) quickActions.push('copyLink');
      if (canCreateEstimate) quickActions.push('duplicate');
      overflow.previewPDF = false;
      overflow.copyLink = false;
      if (canCreateEstimate) overflow.duplicate = false;
    }
  }

  return {
    isEditable,
    isSendable,
    isActionable,
    isApproved,
    isApprovalDismissible,
    depositDue,
    depositPreApproveDue,
    overflow,
    quickActions,
  };
}
